import { richToHtml } from './RichText';
import { pathKind, resolvePath } from './utils';
import { phaseLabel, PHASE_LABEL_LIMIT } from './phaseHeader';

export interface InlineTextTarget {
  path: string;
  blockPath: string;
  value: string;
  rich: boolean;
  prefix: string;
  nodes: SVGTextElement[];
  /** Literal metadata bypasses the rich-text entity encoding used by node text. */
  literal?: boolean;
  allowEmpty?: boolean;
  maxLength?: number;
}

/** Editor-only bindings for existing posters. Never rewrite/export the SVG.
 * Match a COMPLETE field inside its own semantic block; ambiguous, clipped,
 * generated and stale text deliberately stays in the structure editor.
 */
export function bindInlineText(stage: HTMLElement, spec: any): Map<Element, InlineTextTarget> {
  const bindings = new Map<Element, InlineTextTarget>();
  // Wrapping may break just after an opening bracket (e.g. before a bold
  // email address). The visual line break must not make that field uneditable.
  const normalize = (s: string) => s.replace(/\s+/g, ' ').replace(/([([{])\s+/g, '$1').replace(/\s+([)\]},.;:!?])/g, '$1').trim();
  const plain = (s: string) => {
    const el = document.createElement('div'); el.innerHTML = richToHtml(s);
    return normalize(el.textContent || '');
  };
  stage.querySelectorAll('[data-inline-text]').forEach(el => el.removeAttribute('data-inline-text'));
  stage.querySelectorAll<SVGGElement>('[data-path]').forEach(group => {
    const blockPath = group.getAttribute('data-path')!;
    const value = resolvePath(spec, blockPath)?.value;
    const kind = /^cases\.\d+\.(?:lanes\.\d+\.)?nodes\.\d+\.branch\.nodes\.\d+$/.test(blockPath) ? 'branchNode' : pathKind(blockPath);
    if (!value || !kind) return;
    const fields: (Omit<InlineTextTarget, 'nodes'> & { displayed?: string })[] = [];
    const add = (key: string, rich = false, bullets = false, decorate?: (value: string) => string) => {
      const path = key ? `${blockPath}.${key}` : blockPath;
      const v = resolvePath(spec, path)?.value;
      if (Array.isArray(v)) { v.forEach((_, i) => add(key ? `${key}.${i}` : String(i), rich, bullets)); return; }
      if (typeof v !== 'string' || !v.trim()) return;
      const prefix = bullets && /^(## |- )/.test(v) ? v.match(/^(## |- )/)![0] : '';
      fields.push({ path, blockPath, value: v, rich, prefix, displayed: decorate?.(plain(v)) });
    };
    const apis = () => (value.apis || []).forEach((_: any, i: number) => {
      add(`apis.${i}.service`, false, false, v => `API: ${v}`);
      add(`apis.${i}.purpose`, false, false, v => `(${v})`);
    });
    switch (kind) {
      case 'meta':
        ['title', 'subtitle', 'phase_title', 'phase_summary', 'footer'].forEach(k => add(k));
        if (typeof value.phase_title === 'string' && value.phase_title.trim() && typeof phaseLabel(value) === 'string' && phaseLabel(value).trim()) {
          fields.push({ path: 'meta.phase_label', blockPath, value: phaseLabel(value), rich: false,
            prefix: '', literal: true, allowEmpty: true, maxLength: PHASE_LABEL_LIMIT });
        }
        break;
      case 'legend':
        ['user.label', 'user.sublabel', 'dashboard', 'nucleus'].forEach(k => add(k));
        (value.case_chips || []).forEach((_: any, i: number) => add(`case_chips.${i}.lines`)); break;
      case 'block': add('title'); add('bullets', true, true); break;
      case 'case':
        add('title'); add('intro', true, true);
        (value.lanes || []).forEach((lane: any, i: number) => {
          if (lane.role === 'exception') add(`lanes.${i}.exception_label`);
          else if (lane.fan_label) add(`lanes.${i}.fan_label`);
          else add(`lanes.${i}.title`, false, false, v => `[${plain(lane.tag || '')}] ${v}`);
        });
        break;
      case 'lane': add('title', false, false, v => `[${plain(value.tag || '')}] ${v}`); add('intro', true, true); break;
      case 'node':
        add('title'); add('desc', true, true);
        apis();
        ['loop.label', 'loop.note', 'loop.branch_label', 'branch.label', 'branch.note'].forEach(k => add(k));
        (value.branches || []).forEach((_: any, i: number) => { add(`branches.${i}.lines`); add(`branches.${i}.label`); });
        break;
      case 'branchNode': add('title'); add('lines', true, true); break;
      case 'final': add('title'); add('paras', true); add('items', true, true); add('tail', true); apis(); break;
      case 'table': add('title'); (value.columns || []).forEach((_: any, i: number) => add(`columns.${i}.name`)); break;
      case 'tableRow': add('', true); break;
      default: return;
    }
    // The renderer places next-step and forward-loop captions outside their
    // node group, in the containing case/lane. Only full unique matches bind.
    if (kind === 'case' || kind === 'lane') (value.nodes || []).forEach((_: any, i: number) => {
      ['next_label', 'loop.branch_label', 'loop.note'].forEach(k => add(`nodes.${i}.${k}`));
    });
    const texts = Array.from(group.querySelectorAll<SVGTextElement>('text')).filter(node => node.closest('[data-path]') === group);
    const proposals: InlineTextTarget[] = [];
    for (const field of fields) {
      const expected = field.displayed || (field.literal ? normalize(field.value) : plain(field.value.slice(field.prefix.length)));
      if (!expected) continue;
      const matches: SVGTextElement[][] = [];
      // New renders identify the label explicitly, even when its text equals
      // the title. Legacy posters still use the conservative unique match.
      const annotated = field.path === 'meta.phase_label'
        ? texts.filter(node => node.getAttribute('data-field') === field.path) : [];
      if (annotated.length) {
        const lines = annotated.map(node => node.textContent || '');
        // A long word can be split within a normally word-wrapped label.
        // The semantic annotation makes whitespace-only differences safe.
        if (lines.join('').replace(/\s/g, '') === expected.replace(/\s/g, '')) {
          proposals.push({ ...field, nodes: annotated });
        }
        continue;
      }
      const candidates = texts.filter(node => !node.hasAttribute('data-field'));
      for (let start = 0; start < candidates.length; start++) {
        let combined = '';
        const nodes: SVGTextElement[] = [];
        for (let end = start; end < candidates.length; end++) {
          const part = normalize(candidates[end].textContent || '');
          if (!part || part === '•' || part === '–') break;
          combined = normalize(combined ? combined + ' ' + part : part);
          nodes.push(candidates[end]);
          if (combined === expected) matches.push([...nodes]);
          if (!expected.startsWith(combined) || combined.length >= expected.length) break;
        }
      }
      if (matches.length === 1) proposals.push({ ...field, nodes: matches[0] });
    }
    for (const target of proposals) {
      if (proposals.some(other => other !== target && other.nodes.some(node => target.nodes.includes(node)))) continue;
      target.nodes.forEach(node => { bindings.set(node, target); node.setAttribute('data-inline-text', target.path); });
    }
  });
  return bindings;
}

export function resolveInlineTextValue(spec: any, path: string): any {
  return path === 'meta.phase_label' && spec?.meta && typeof spec.meta === 'object'
    ? phaseLabel(spec.meta) : resolvePath(spec, path)?.value;
}

export function replaceInlineText(spec: any, target: Pick<InlineTextTarget, 'path' | 'value'>, value: string): any {
  const isPhaseLabel = target.path === 'meta.phase_label' && spec.meta && typeof spec.meta === 'object';
  const current = resolveInlineTextValue(spec, target.path);
  if (current !== target.value) throw new Error('This text changed. Close the editor and open it again.');
  const copy = JSON.parse(JSON.stringify(spec));
  const parts = target.path.split('.');
  const key = parts.pop()!;
  const parent = resolvePath(copy, parts.join('.'))?.value;
  if (!parent || (!isPhaseLabel && !Object.prototype.hasOwnProperty.call(parent, key))) throw new Error('This text is no longer available.');
  parent[key] = value;
  return copy;
}
